import { ErrorHandler } from 'components/errors/ErrorHandler';
import { PeriodSelector } from 'components/period-selection';
import { LoadingSpinner } from 'components/spinners';
import {
  getUserColumnsByKeys,
  UserTableColumns
} from 'components/tables/users-table/defaults/user-columns';
import { ExpandedBlock } from 'components/tables/users-table/renderers/ExpandedBlock';
import { UserTable } from 'components/tables/users-table/UserTable';
import { Disclaimer } from 'components/text';
import { ApiError, isApiError } from 'errors';
import { trackException } from 'libs/telemetry';
import { Month } from 'libs/time';
import { triggerWarningToast } from 'libs/toasts';
import { useTranslation } from 'libs/translations';
import _ from 'lodash';
import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchDownlineForPath } from 'services';
import AuthenticationService from 'services/AuthenticationService';
import { IUserProfile } from 'types';
import { decodeAsArray, encodeArray } from 'utils';

import { useDownlineContext } from './DownlineContext';
import { useUserDownline } from './hooks/useUserDownline';
import { UplineList } from './UplineList';

export const DownlineExplorer: FC = () => {
  const { selectedPeriod, onPeriodChange } = useDownlineContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { encodedUsersSelectedPath } = useParams();
  const isCurrentMonth = selectedPeriod.getTime() == Month.today().getTime();

  const { selectedUserId, goToUserDownline } = useUserDownline();

  const [isLoadingDownline, setLoadingDownline] = useState<boolean>(false);

  const [upline, setUpline] = useState<ReadonlyArray<IUserProfile>>([]);
  const [selectedUser, setSelectedUser] = useState<IUserProfile | undefined>();
  const [downline, setDownline] = useState<ReadonlyArray<IUserProfile>>([]);

  const [downlineComputationError, setDownlineComputationError] =
    useState<unknown>(undefined);

  const redirectToBaseCase = () => {
    const encodedState = encodeArray([AuthenticationService.user.userId]);
    navigate(`/downline/${encodedState}`);
  };

  useEffect(() => {
    if (_.isUndefined(encodedUsersSelectedPath)) {
      redirectToBaseCase();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encodedUsersSelectedPath]);

  useEffect(() => {
    if (_.isUndefined(encodedUsersSelectedPath)) {
      return;
    }

    const selectedUsersPath = decodeAsArray(encodedUsersSelectedPath);

    // Show the loading indicator while the new downline is computed
    setLoadingDownline(true);
    setDownlineComputationError(undefined);

    fetchDownlineForPath(selectedUsersPath, selectedPeriod)
      .then(({ upline, selectedUser, downline }) => {
        setDownline(downline);
        setSelectedUser(selectedUser);
        setUpline(
          upline.filter(({ userId }) => userId !== selectedUser?.userId)
        );
      })
      .catch(err => {
        setDownlineComputationError(err);
        trackException(
          err,
          'DownlineExplorer.tsx',
          `Failed while fetching downline path [${selectedUsersPath.join(
            ', '
          )}]`
        );

        if (downline.length === 0) {
          // If we failed to open the page in a certain state, redirect to root
          redirectToBaseCase();
          return;
        }

        if (isApiError(err)) {
          if (err.status === 403) return;

          if (err.status === 404)
            return navigate(
              `/downline/pathTo/structure?userId=${selectedUserId}`
            );
        }

        triggerWarningToast(
          t(
            'Something went wrong. Cannot load your team members right now. Please try again later or contact the support team.'
          )
        );
        console.error('Downline computation error.', err);
      })
      .finally(() => setLoadingDownline(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encodedUsersSelectedPath, selectedPeriod]);

  const users = useMemo(() => {
    return [
      ...upline.map(user => ({
        ...user,
        structureType: 'upline' as const
      })),
      ...(selectedUser
        ? [
            {
              ...selectedUser,
              structureType: 'selectedUser' as const
            }
          ]
        : []),
      ...downline.map(user => ({
        ...user,
        structureType: 'downline' as const
      }))
    ];
  }, [upline, selectedUser, downline]);

  const tableColumns = useMemo(() => {
    const columnKeys = [
      UserTableColumns.Actions,
      UserTableColumns.User,
      UserTableColumns.CheckAssurance,
      UserTableColumns.CurrentQualification,
      UserTableColumns.PreviousQualification,
      UserTableColumns.HighestQualification,
      UserTableColumns.FitlineClubMember,
      UserTableColumns.Autoships,
      UserTableColumns.PersonalSales,
      UserTableColumns.SixLevelSales,
      UserTableColumns.SixLevelTrend,
      UserTableColumns.StructureSales,
      UserTableColumns.StructureTrend
    ];

    if (isCurrentMonth) {
      columnKeys.push(UserTableColumns.OpenAutoshipVolume);
    }

    return getUserColumnsByKeys(columnKeys);
  }, [isCurrentMonth]);

  return (
    <section className="h-full space-y-md">
      <div className="flex justify-end">
        <PeriodSelector
          minDate={selectedUser && selectedUser.registrationDate}
          onPeriodChange={onPeriodChange}
        />
      </div>

      {isLoadingDownline ? (
        <div className="mt-sm flex flex-row justify-center">
          <LoadingSpinner smallSize />
        </div>
      ) : _.isUndefined(downlineComputationError) ? (
        <div className="space-y-4">
          {upline.length > 0 && (
            <div className="space-y-1">
              <p className="text-silver pmi-caption">
                {t('Upline of {{name}}', {
                  name: selectedUser?.name
                })}
              </p>
              <UplineList
                upline={upline}
                onExploreUserDownline={goToUserDownline}
              />
            </div>
          )}
          <div className="overflow-x-auto">
            <div className="py-2 inline-block w-full">
              <UserTable
                columns={tableColumns}
                users={users}
                renderExpandedBlock={user => (
                  <ExpandedBlock userId={user.userId} />
                )}
                tableRowClassName={user =>
                  `${
                    user.structureType === 'selectedUser'
                      ? 'bg-secondary'
                      : user.structureType === 'upline'
                      ? 'bg-body'
                      : `bg-white border-b-2 border-body`
                  }`
                }
              />
            </div>
          </div>
        </div>
      ) : downlineComputationError instanceof Error &&
        downlineComputationError.name === 'ApiError' &&
        (downlineComputationError as ApiError).status === 403 ? (
        <div className="w-downline m-auto">
          <Disclaimer
            message={t(
              'Unfortunately, we are unable to grant access to the requested data.'
            )}
          />
        </div>
      ) : (
        <div className="w-full flex justify-center">
          <ErrorHandler err={downlineComputationError} />
        </div>
      )}
    </section>
  );
};
